import { FC } from 'react';
import { Listbox } from '@headlessui/react';

import { ReactComponent as DropdownArrowIcon } from 'assets/icons/arrow-down.svg';
import CommonCheckBox from 'components/CommonCheckBox/CommonCheckBox';
import { IClinicApiKeyClinic } from 'interfaces/clinicApiKeys';

const SELECT_ALL_ID = 'api-key-clinics-select-all';

interface ApiKeyClinicDropdownProps {
  clinics: IClinicApiKeyClinic[];
  currentClinicId: string;
  value: string[];
  onChange: (clinicIds: string[]) => void;
}

const ApiKeyClinicDropdown: FC<ApiKeyClinicDropdownProps> = ({
  clinics,
  currentClinicId,
  value,
  onChange,
}) => {
  const selectedClinicIds = [currentClinicId, ...value];
  const isDisabled = clinics.length <= 1;

  const otherClinicIds = clinics
    .map((clinic) => clinic.id)
    .filter((clinicId) => clinicId !== currentClinicId);
  const isAllSelected = otherClinicIds.every((clinicId) =>
    value.includes(clinicId)
  );
  const isSomeSelected = !isAllSelected && value.length > 0;

  const getButtonLabel = () => {
    if (selectedClinicIds.length === 1) {
      return (
        clinics.find((clinic) => clinic.id === currentClinicId)?.name ?? ''
      );
    }

    return `${selectedClinicIds.length} practices`;
  };

  const handleChange = (clinicIds: string[]) =>
    onChange(clinicIds.filter((clinicId) => clinicId !== currentClinicId));

  const handleSelectAll = () => onChange(isAllSelected ? [] : otherClinicIds);

  return (
    <div className="flex flex-col gap-y-0.8">
      <span className="font-bold">Practices</span>
      <Listbox
        value={selectedClinicIds}
        onChange={handleChange}
        disabled={isDisabled}
        as="div"
        className="relative"
        multiple
      >
        <Listbox.Button
          data-testid="api-key-clinics-button"
          className="flex flex-row justify-between items-center w-full h-[4.8rem] py-1.3 px-1.6 rounded-[1rem] shadow-primary border border-transparent enabled:hover:border-magenta enabled:hover:shadow-elevation-10 disabled:cursor-not-allowed disabled:text-mid-grey"
        >
          <span className="truncate text-left">{getButtonLabel()}</span>
          <DropdownArrowIcon width={14} height={14} />
        </Listbox.Button>
        <Listbox.Options className="absolute top-5.6 z-[9999] w-full max-h-[24rem] overflow-auto overscroll-contain scrollbar bg-white rounded-[1rem] flex flex-col py-1.6 px-2 shadow-primary">
          <div className="mb-1.6 hover:text-magenta w-full min-h-[2.7rem] truncate">
            <CommonCheckBox
              id={SELECT_ALL_ID}
              isChecked={isAllSelected}
              ref={(element: HTMLInputElement | null) => {
                if (element) {
                  element.indeterminate = isSomeSelected;
                }
              }}
              onChange={handleSelectAll}
            >
              <label htmlFor={SELECT_ALL_ID} className="text-14 cursor-pointer">
                Select all
              </label>
            </CommonCheckBox>
          </div>
          {clinics.map((clinic) => {
            const isCurrentClinic = clinic.id === currentClinicId;

            return (
              <Listbox.Option
                key={clinic.id}
                value={clinic.id}
                disabled={isCurrentClinic}
                as="button"
                type="button"
                className="mb-1.6 last:mb-0 hover:text-magenta w-full text-left min-h-[2.7rem] truncate disabled:hover:text-darkest-grey"
              >
                <CommonCheckBox
                  isChecked={selectedClinicIds.includes(clinic.id)}
                  disabled={isCurrentClinic}
                >
                  <span className="text-14">{clinic.name}</span>
                </CommonCheckBox>
              </Listbox.Option>
            );
          })}
        </Listbox.Options>
      </Listbox>
      <span className="text-dim-grey">
        {isDisabled
          ? 'Only active practices in the same group, linked to the PMS, that you administer can be added'
          : 'The key can read data of every selected practice'}
      </span>
    </div>
  );
};

export default ApiKeyClinicDropdown;
